import { Injectable, Injector } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { GlobalService } from './global.service';

/**
 * Estratégia de preload seletiva e inteligente
 *
 * BENEFÍCIO:
 * - Reduz drasticamente o consumo de rede e CPU em visitantes anónimos
 * - Não descarrega módulos pesados de Administração (4.2 MB) a quem não tem permissões
 * - Garante que utilizadores autenticados continuam a ter pré-carregamento rápido das suas áreas
 */
@Injectable({
  providedIn: 'root',
})
export class SelectivePreloadStrategy implements PreloadingStrategy {
  constructor(private injector: Injector) {}

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!route.data?.['preload']) {
      return of(null);
    }

    try {
      // Obter serviços via Injector para prevenir qualquer dependência circular com o Router
      const authService = this.injector.get(AuthService, null);
      const globalService = this.injector.get(GlobalService, null);
      const user = globalService?.loggedUserClass;
      const isLogged = authService && user ? authService.isLoggedin(user) : false;
      const isAdmin = isLogged && !!user?.Admin;
      const isSiteManagement = isLogged && (isAdmin || !!user?.SiteManagement);
      const isIntermediary = isLogged && (isSiteManagement || !!user?.IntermediaryArea);

      // 1. Área de Gestão Pesada (Backoffice Admin): apenas pré-carrega para administradores/gestão
      if (route.path === 'sitemanagement' && !isSiteManagement) {
        return of(null);
      }

      // 2. Área do Mediador/Parceiro: apenas pré-carrega para utilizadores autenticados com acesso
      if (route.path === 'intermediaryarea' && !isIntermediary) {
        return of(null);
      }

      // 3. Área do Cliente: apenas pré-carrega para utilizadores autenticados
      if (route.path === 'clientarea' && !isLogged) {
        return of(null);
      }
    } catch (_) {
      // Em caso de erro na resolução de permissões, não bloqueia e prossegue
    }

    // Delay de 4s para estabilizar a renderização inicial da página
    return timer(4000).pipe(switchMap(() => load()));
  }
}
